import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Button, Checkbox, message, Modal, Tag, Empty, Spin } from "antd";
import { EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { postRequest } from "../../Api";
import { apiRoutes } from "../../Api/apiRoutes";
import { setLoader } from "../../Redux/Reducers/gernalSlice";
import { VEH_AGREEMENT_URL } from "../../Api/constants";
import appColors from "../../config/appColors";

const eventColors = {
    generated: "blue",
    signed: "green",
    extended: "orange",
    regenerated: "purple",
};

const formatLabel = (type) => {
    if (!type) return "Unknown";
    return String(type).charAt(0).toUpperCase() + String(type).slice(1);
};

const AgreementHistoryDrawer = ({ open, onClose, agreement }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.authReducer.user);

    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [versions, setVersions] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [previewPath, setPreviewPath] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const agreementId = agreement?.agreement_id;

    const loadHistory = () => {
        if (!agreementId) return;
        setLoading(true);
        const onSuccess = (res) => {
            setLoading(false);
            const data = res?.data || {};
            setMeta(data);
            const list = Array.isArray(data.versions) ? data.versions : [];
            setVersions(list);
            setSelectedIds([]);
            const current = list.find((v) => v.is_current) || list[0];
            setPreviewPath(current?.pdf_path || null);
        };
        const onError = (err) => {
            setLoading(false);
            message.error(err?.data?.error || "Failed to load agreement history");
        };
        postRequest(
            { token: user?.token, agreement_id: agreementId },
            apiRoutes.getAgreementHistory,
            onSuccess,
            onError
        );
    };

    useEffect(() => {
        if (open && agreementId) {
            loadHistory();
        }
        if (!open) {
            setVersions([]);
            setSelectedIds([]);
            setPreviewPath(null);
            setMeta(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, agreementId]);

    const selectableIds = useMemo(
        () => versions.filter((v) => !v.is_current).map((v) => v.version_id),
        [versions]
    );

    const allSelectableChecked =
        selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

    const toggleSelectAll = () => {
        if (allSelectableChecked) {
            setSelectedIds([]);
        } else {
            setSelectedIds(selectableIds);
        }
    };

    const toggleOne = (version) => {
        if (version.is_current) return;
        setSelectedIds((prev) =>
            prev.includes(version.version_id)
                ? prev.filter((id) => id !== version.version_id)
                : [...prev, version.version_id]
        );
    };

    const doDelete = () => {
        if (!selectedIds.length) return;
        setConfirmDelete(false);
        dispatch(setLoader(true));
        const onSuccess = (res) => {
            message.success(res?.message || "Deleted from history");
            loadHistory();
            dispatch(setLoader(false));
        };
        const onError = (err) => {
            dispatch(setLoader(false));
            message.error(err?.data?.error || "Delete failed");
        };
        postRequest(
            {
                token: user?.token,
                agreement_id: agreementId,
                version_ids: JSON.stringify(selectedIds),
            },
            apiRoutes.deleteAgreementVersions,
            onSuccess,
            onError
        );
    };

    const title = (
        <div>
            <div style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16 }}>
                Agreement History
            </div>
            <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#666", marginTop: 4 }}>
                {[meta?.cust_name || agreement?.name, meta?.veh_name || agreement?.veh_name, meta?.reg_no || agreement?.reg_no]
                    .filter(Boolean)
                    .join(" · ")}
                {agreementId ? ` · #${agreementId}` : ""}
            </div>
        </div>
    );

    return (
        <>
            <Drawer
                title={title}
                placement="right"
                width={Math.min(920, typeof window !== "undefined" ? window.innerWidth - 40 : 920)}
                onClose={onClose}
                open={open}
                destroyOnClose
            >
                <Spin spinning={loading}>
                    <div
                        style={{
                            display: "flex",
                            gap: 16,
                            minHeight: 420,
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 10,
                                }}
                            >
                                <div style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13 }}>
                                    Versions ({versions.length})
                                </div>
                                {selectableIds.length > 0 && (
                                    <Checkbox
                                        checked={allSelectableChecked}
                                        indeterminate={
                                            selectedIds.length > 0 && !allSelectableChecked
                                        }
                                        onChange={toggleSelectAll}
                                    >
                                        Select archived
                                    </Checkbox>
                                )}
                            </div>

                            {versions.length === 0 && !loading ? (
                                <Empty description="No history yet" />
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {versions.map((v) => {
                                        const active = previewPath === v.pdf_path;
                                        return (
                                            <div
                                                key={v.version_id}
                                                onClick={() => setPreviewPath(v.pdf_path)}
                                                style={{
                                                    border: active
                                                        ? `1.5px solid ${appColors.primary}`
                                                        : "1px solid #e8e8e8",
                                                    borderRadius: 8,
                                                    padding: 12,
                                                    cursor: "pointer",
                                                    background: active ? "#f7fafc" : "#fff",
                                                    fontFamily: "Poppins",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 8,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={selectedIds.includes(v.version_id)}
                                                        disabled={!!v.is_current}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => toggleOne(v)}
                                                        style={{ marginTop: 2 }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 8,
                                                                flexWrap: "wrap",
                                                            }}
                                                        >
                                                            <span style={{ fontWeight: 600, fontSize: 13 }}>
                                                                v{v.version_no}
                                                            </span>
                                                            <Tag color={eventColors[v.event_type] || "default"}>
                                                                {formatLabel(v.event_type)}
                                                            </Tag>
                                                            {v.is_current && (
                                                                <Tag color="geekblue">Current</Tag>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                                                            End date: {v.expected_end_date || "—"}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                                                            {v.created_at || ""}
                                                            {v.created_by_name ? ` · ${v.created_by_name}` : ""}
                                                        </div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <a
                                                                href={VEH_AGREEMENT_URL + v.pdf_path}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ fontSize: 12 }}
                                                            >
                                                                <EyeOutlined /> Open PDF
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedIds.length > 0 && (
                                <div
                                    style={{
                                        marginTop: 16,
                                        padding: 12,
                                        borderTop: "1px solid #eee",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    <span style={{ fontFamily: "Poppins", fontSize: 12 }}>
                                        {selectedIds.length} selected
                                    </span>
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => setConfirmDelete(true)}
                                    >
                                        Delete selected
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                flex: "1 1 360px",
                                minWidth: 280,
                                border: "1px solid #eee",
                                borderRadius: 8,
                                overflow: "hidden",
                                background: "#fafafa",
                                minHeight: 420,
                            }}
                        >
                            {previewPath ? (
                                <iframe
                                    title="Agreement PDF preview"
                                    src={VEH_AGREEMENT_URL + previewPath}
                                    style={{ width: "100%", height: "100%", minHeight: 420, border: "none" }}
                                />
                            ) : (
                                <div
                                    style={{
                                        height: "100%",
                                        minHeight: 420,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#999",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Select a version to preview
                                </div>
                            )}
                        </div>
                    </div>
                </Spin>
            </Drawer>

            <Modal
                title="Delete from history?"
                open={confirmDelete}
                onOk={doDelete}
                onCancel={() => setConfirmDelete(false)}
                okText="Delete"
                okButtonProps={{ danger: true }}
            >
                <p style={{ fontFamily: "Poppins", fontSize: 13 }}>
                    {selectedIds.length} archived PDF version(s) will be removed from history.
                    The <b>current</b> agreement PDF will not be deleted.
                </p>
            </Modal>
        </>
    );
};

export default AgreementHistoryDrawer;
